import { FREE_MONTHLY_CREDITS } from "../../../constants/credits";

const tool = {
  slug: "listing-description-generator",
  name: "Listing Description Generator",
  title: "Free Listing Description Generator: Paste Your Property, Get a Draft",
  metaTitle: "Free Listing Description Generator | Listino",
  description:
    "Generate an MLS-ready listing description draft from a few property details — free, no signup. Then learn how to make it actually sell.",
  status: "coming-soon",
  primaryCtaPath: "/get-started",
  tldr: "Enter a few property details and get a clean, MLS-ready listing-description draft in seconds — free, no signup. It's a fast first draft you can edit; a Listino listing prep tailors it to your comps and buyer.",
  blocks: [
    { type: "h2", id: "what-it-does", text: "What the generator does" },
    {
      type: "p",
      spans: [
        "Give it the basics — property type, beds and baths, square footage, a few standout features — and it returns a structured 150–250 word listing description you can paste into the MLS and refine. It handles the blank-page problem so you can spend your time editing instead of starting from scratch.",
      ],
    },
    {
      type: "h2",
      id: "what-makes-a-good-description",
      text: "What makes a listing description actually sell",
    },
    {
      type: "p",
      spans: [
        "A good description does three things a generic one doesn't: it leads with the single most compelling feature, it sells the lifestyle rather than re-listing the spec sheet, and it uses concrete specifics instead of vague adjectives. Our full guide on ",
        {
          text: "how to write a listing description that sells",
          href: "/resources/how-to-write-a-listing-description-that-sells",
        },
        " walks through the structure, and ",
        { text: "words that sell a house", href: "/resources/words-that-sell-a-house" },
        " covers the language to use and avoid.",
      ],
    },
    {
      type: "callout",
      spans: [
        "A generator gets you a clean first draft fast. It can't see your comps, your photos, or your target buyer — so always edit the draft to lead with what's genuinely special about this home.",
      ],
    },
    { type: "h2", id: "how-to-use-the-draft", text: "How to use the draft" },
    {
      type: "ol",
      items: [
        "Generate the draft from your property details.",
        "Move your single best feature into the first sentence.",
        "Cut any adjective you can't back up with a specific (swap 'spacious' for the actual room dimension or use).",
        "Add one lifestyle line that helps a buyer picture living there.",
        "Read it against your photos — the words and images should tell the same story.",
      ],
    },
    { type: "h2", id: "free-vs-full", text: "Free draft vs. a tailored rewrite" },
    {
      type: "p",
      spans: [
        "The generator is a great starting point. A full ",
        { text: "Listino listing prep", href: "/get-started" },
        " goes further: it writes the description against your actual comparable sales and likely buyer, and pairs it with photo sequencing and a comparative market analysis — the difference between a draft and a listing strategy.",
      ],
    },
  ],
  faqs: [
    {
      question: "Is the listing description generator free?",
      answer: [
        {
          type: "p",
          spans: [
            `Yes, and there's no signup to try it. A full Listino listing prep, which tailors the description to your comps and includes photo and pricing guidance, is free to start too: every Listino account includes ${FREE_MONTHLY_CREDITS.toLocaleString("en-US")} credits a month, enough to prepare a Listing Analysis on us.`,
          ],
        },
      ],
    },
    {
      question: "Can I use the description on Zillow, the MLS, or Realtor.com?",
      answer: [
        {
          type: "p",
          spans: [
            "Yes — it's plain text you can paste anywhere. Always review it against your local MLS rules and edit for accuracy before publishing.",
          ],
        },
      ],
    },
    {
      question: "Will the description be accurate to my home?",
      answer: [
        {
          type: "p",
          spans: [
            "It's only as accurate as the details you provide, and it's a starting draft — you should always edit it to make sure every claim is true and leads with what's genuinely best about the property.",
          ],
        },
      ],
    },
  ],
};

export default tool;
